
import * as d3 from 'd3';
import { DateTime, Duration, Interval } from 'luxon';

// These cutoffs are as a function of viewport size
const _mtd_cutoffs = [    'PT12H', 'PT36H', 'PT108H',  'P10D',  'P45D', 'P3Y', 'P35Y'   ];
const _mtd_values = ['PT30M',  'PT1H',   'PT3H',  'PT12H', 'P1D',  'P1M', 'P1Y', 'P10Y'];
const mtd_cutoffs = _mtd_cutoffs.map(s => Duration.fromISO(s));
const mtd_values = _mtd_values.map(s => Duration.fromISO(s));

const _dintv_cutoffs = [   'P3D',   'P10D',  'P1Y6M',  'P20Y'   ];
const _dintv_values = ['PT1H',  'PT3H',   'P1D',  'P1M',  'P1Y'];
const dintv_cutoffs = _dintv_cutoffs.map(s => Duration.fromISO(s));
const dintv_values = _dintv_values.map(s => Duration.fromISO(s));

// These cutoffs are as a function of mtd (major tick duration) size
const _blf_cutoffs = [    'PT1H', 'PT24H', 'P7D', 'P1Y'   ];
const _blf_values = ['%H%MZ',  '%HZ',   '%d',  '%m',  null];
const blf_cutoffs = _blf_cutoffs.map(s => Duration.fromISO(s));
const blf_values = _blf_values.map(s => s === null ? () => '' : d3.utcFormat(s));

const _tlf_cutoffs = [        'P1D',    'P7D'    ];
const _tlf_values = ['%Y-%m-%d',  '%Y-%m',   '%Y'];
const tlf_cutoffs = _tlf_cutoffs.map(s => Duration.fromISO(s));
const tlf_values = _tlf_values.map(s => s === null ? () => '' : d3.utcFormat(s));
const tlf_checks = [jsdt => jsdt.getUTCHours() == 0 && jsdt.getUTCMinutes() == 0, 
                    jsdt => jsdt.getUTCDate() == 1,
                    jsdt => jsdt.getUTCMonth() == 0 && jsdt.getUTCDate() == 1]

const orient_cutoff = Duration.fromISO('P1Y');

const stepwise = (x_val, x_cutoffs, y_values) => {
    let y_val = null;

    x_cutoffs.some((xc, ic) => {
        if (x_val < xc) {
            y_val = y_values[ic];
            return true;
        }
    });

    if (y_val === null) {
        y_val = y_values[y_values.length - 1];
    }

    return y_val;
}

const luxon_d3_conversion = {'years': d3.utcYear, 'months': d3.utcMonth, 'days': d3.utcDay, 
                             'hours': d3.utcHour, 'minutes': d3.utcMinute};

class MultiscaleTimeAxis {
    constructor(intv, xbnds, t_limits, t_buffer, min_scale) {
        this.intv = intv;
        [this.xlb, this.xub] = xbnds;
        this.t_limits = t_limits;
        this.t_buffer = t_buffer;
        this.min_scale = min_scale;

        const width = this.xub - this.xlb;

        this.t_bnds_intv = this.intv.getIntervals()[0];
        const intv_dur = Math.max(1, 700 / width) * this.t_bnds_intv.toDuration();

        this.mtd = stepwise(intv_dur, mtd_cutoffs, mtd_values);
        this.data_dintv = stepwise(intv_dur, dintv_cutoffs, dintv_values);
        this.bl_format = stepwise(this.mtd, blf_cutoffs, blf_values);
        this.tl_format = stepwise(this.mtd, tlf_cutoffs, tlf_values);
        this.tl_check = stepwise(this.mtd, tlf_cutoffs, tlf_checks);
        this.draw_orient = this.mtd < orient_cutoff;

        const zoom_limit_intv = this.getPaddedBounds();
        const scale_intv = zoom_limit_intv.intersection(this.t_bnds_intv);

        this.scale = d3.scaleUtc()
            .domain([scale_intv.start.toJSDate(), scale_intv.end.toJSDate()])
            .range([this.xlb, this.xub]);
    }

    withMinScale(min_scale) {
        return new MultiscaleTimeAxis(this.intv, [this.xlb, this.xub], this.t_limits, this.t_buffer, min_scale);
    }

    withInterval(intv) {
        return new MultiscaleTimeAxis(intv, [this.xlb, this.xub], this.t_limits, this.t_buffer, this.min_scale);
    }

    withXBounds(xbnds) {
        return new MultiscaleTimeAxis(this.intv, xbnds, this.t_limits, this.t_buffer, this.min_scale);
    }

    getBarSubintervals(cday_offset) {
        let data_dintv = this.data_dintv;
        if (this.min_scale !== undefined) {
            data_dintv = data_dintv > this.min_scale ? data_dintv : this.min_scale;
        }
        return this.intv.getSubintervals(data_dintv, cday_offset);
    }

    map(dt) {
        return this.scale(dt.toJSDate());
    }

    mapInverse(x) {
        return DateTime.fromJSDate(this.scale.invert(x), {'zone': 'UTC'});
    }

    getTicks() {
        var d3_ticks;
        for (let comp in luxon_d3_conversion) {
            if (this.mtd[comp] > 0) {
                d3_ticks = luxon_d3_conversion[comp].every(this.mtd[comp]);
                break
            }
        }
        return d3_ticks;
    }

    formatTick(jsdt) {
        let str = this.bl_format(jsdt);

        if (this.tl_check(jsdt)) {
            if (str != "") {
                str += " ";
            }
            str += this.tl_format(jsdt);
        }
        return str;
    }

    getPaddedBounds() {
        const width = this.xub - this.xlb;
        const bounds_dur = this.t_bnds_intv.toDuration() > this.t_limits.toDuration() ? this.t_limits.toDuration() : this.t_bnds_intv.toDuration();
        const buffer_dur = this.t_buffer / (width - 2 * this.t_buffer) * bounds_dur;
    
        const earliest_dt = this.t_limits.start.minus(buffer_dur);
        const latest_dt = this.t_limits.end.plus(buffer_dur);
    
        return Interval.fromDateTimes(earliest_dt, latest_dt);
    }

    getOrientLabel() {
        if (this.draw_orient) {
            return this.tl_format(this.t_bnds_intv.start.toJSDate());
        }
        return null;
    }
}

export default MultiscaleTimeAxis;